import React, { useState, useEffect } from 'react';
import { Row, Col, Card, CardBody, Button, Modal, ModalHeader } from 'reactstrap';
import SweetAlert from 'react-bootstrap-sweetalert';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

const SortableItemList = ({
    title,
    items,
    getKey,
    getLabel,
    renderLabel,
    loading = false,
    droppableId,
    onReorder,
    onDelete,
    canDelete = () => true,
    deleteBlockedMessage,
    deleteWarning,
    createLabel = 'Create',
    editLabel = 'Edit',
    createTitle = 'Create Item',
    editTitle = 'Edit Item',
    emptyMessage = 'No items exist.',
    renderForm,
}) => {
    const [localItems, setLocalItems] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteBlocked, setDeleteBlocked] = useState(false);

    useEffect(() => {
        setLocalItems([...items]);
    }, [items]);

    const close = () => setModalOpen(false);

    const openCreate = () => {
        setEditingItem(null);
        setModalOpen(true);
    };

    const openEdit = (item) => {
        setEditingItem(item);
        setModalOpen(true);
    };

    const confirmDelete = () => {
        if (!canDelete(deleteTarget)) {
            setDeleteBlocked(true);
            setDeleteTarget(null);
            return;
        }
        Promise.resolve(onDelete(deleteTarget)).then(() => setDeleteTarget(null));
    };

    const onDragEnd = (result) => {
        if (!result.destination) return;
        const reordered = Array.from(localItems);
        const [moved] = reordered.splice(result.source.index, 1);
        reordered.splice(result.destination.index, 0, moved);
        setLocalItems(reordered);
        onReorder(reordered);
    };

    return (
        <>
            {deleteBlocked && deleteBlockedMessage && (
                <SweetAlert
                    error
                    title="Error"
                    onConfirm={() => setDeleteBlocked(false)}
                    showConfirm={false}
                    timeout={2500}
                >
                    {deleteBlockedMessage}
                </SweetAlert>
            )}
            {deleteTarget && (
                <SweetAlert
                    warning
                    title="Confirm"
                    onConfirm={confirmDelete}
                    onCancel={() => setDeleteTarget(null)}
                    showCancel
                    confirmBtnText="Delete"
                    cancelBtnText="Cancel"
                    confirmBtnBsStyle="danger"
                    cancelBtnBsStyle="secondary"
                >
                    {deleteWarning(deleteTarget)}
                </SweetAlert>
            )}

            <Modal isOpen={modalOpen} toggle={close}>
                <ModalHeader toggle={close}>
                    {editingItem ? editTitle : createTitle}
                </ModalHeader>
                <React.Fragment key={editingItem ? getKey(editingItem) : '__create__'}>
                    {renderForm && renderForm(editingItem, close)}
                </React.Fragment>
            </Modal>

            <div className="content">
                <div className="container-fluid">
                    <div className="page-title-box">
                        <Row className="align-items-center">
                            <Col sm="6">
                                <h4 className="page-title">{title}</h4>
                            </Col>
                            <Col sm="6" className="text-right">
                                <Button color="success" onClick={openCreate}>{createLabel}</Button>
                            </Col>
                        </Row>
                    </div>
                    <Row>
                        <Col lg="6">
                            <Card>
                                <CardBody>
                                    {loading ? null : localItems.length > 0 ? (
                                        <DragDropContext onDragEnd={onDragEnd}>
                                            <Droppable droppableId={droppableId}>
                                                {(provided) => (
                                                    <div ref={provided.innerRef} {...provided.droppableProps}>
                                                        {localItems.map((item, index) => (
                                                            <Draggable
                                                                key={getKey(item)}
                                                                draggableId={String(getKey(item))}
                                                                index={index}
                                                            >
                                                                {(provided, snapshot) => (
                                                                    <div
                                                                        ref={provided.innerRef}
                                                                        {...provided.draggableProps}
                                                                        style={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'space-between',
                                                                            padding: '10px 12px',
                                                                            margin: '6px 0',
                                                                            background: snapshot.isDragging ? '#e8f4fd' : '#f8f9fa',
                                                                            border: '1px solid #dee2e6',
                                                                            borderRadius: '4px',
                                                                            ...provided.draggableProps.style,
                                                                        }}
                                                                    >
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                            <span {...provided.dragHandleProps} style={{ cursor: 'grab', color: '#aaa' }}>
                                                                                <i className="fas fa-bars"></i>
                                                                            </span>
                                                                            {renderLabel ? renderLabel(item) : <span>{getLabel(item)}</span>}
                                                                        </div>
                                                                        <div>
                                                                            <Button color="info" size="sm" className="mr-2" onClick={() => openEdit(item)}>{editLabel}</Button>
                                                                            <Button color="danger" size="sm" onClick={() => setDeleteTarget(item)}>Delete</Button>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </Draggable>
                                                        ))}
                                                        {provided.placeholder}
                                                    </div>
                                                )}
                                            </Droppable>
                                        </DragDropContext>
                                    ) : emptyMessage}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </div>
            </div>
        </>
    );
};

export default SortableItemList;
